import React, { useState } from "react";
import { Link } from "react-router-dom";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { OvalLoader } from "../../../components/common/Loader";
import { BRAND, GRADIENTS } from "../../../constant/theme";
import {
  PERIOD_OPTIONS,
  getDisplayName,
  getPeriodSubtitle,
  getProfileImage,
  getSectionTitle,
} from "./topAchieversShared";
const CATEGORY_META = {
  tyfcbs: { subtitle: "Closed business value", icon: "bx-trending-up" },
  fbms: { subtitle: "Meetings completed", icon: "bx-conversation" },
  rfls: { subtitle: "Referrals given", icon: "bx-share-alt" },
  visitors: { subtitle: "Visitors brought", icon: "bx-user-plus" },
  chief_guests: { subtitle: "Guests hosted", icon: "bx-crown" },
  inductions: { subtitle: "New inductees", icon: "bx-user-check" },
  power_dates: { subtitle: "Power dates hosted", icon: "bx-calendar-event" },
};

const getInitials = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const formatMetric = (user, cat) => {
  const raw = user?.[cat.valKey] ?? 0;
  if (cat.id === "tyfcbs") {
    const amount = Number(String(raw).replace(/[₹,\s]/g, ""));
    if (!Number.isNaN(amount)) {
      return `₹${new Intl.NumberFormat("en-IN", {
        maximumFractionDigits: 0,
      }).format(amount)}`;
    }
  }
  return cat.formatVal(raw || 0);
};

const companyOf = (user) => user?.user_company_name || "Company not added";

const tooltipText = (user, cat, rank) =>
  `#${rank} ${user[cat.tooltipNameKey] || user[cat.nameKey]} (${
    user.user_company_name || ""
  }) - ${user.country_name || ""} - ${user.state_name || ""} - ${
    user.region_name || ""
  } - ${user.chapter_name || ""}`;

const CATEGORY_REPORT_ROUTES = {
  tyfcbs: "/reports/tyfcbs",
  fbms: "/reports/one-to-one",
  rfls: "/reports/referrals",
  visitors: "/reports/visitors-general",
  chief_guests: "/reports/visitors-chief-guest",
  power_dates: "/reports/visitors-power-date",
  inductions: "/reports/visitors-inductions",
};

const MemberAvatar = ({ user, cat, tone }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const src = getProfileImage(user, cat);
  const name = getDisplayName(user, cat);
  const className = `ta-v-avatar${tone === "lead" ? " is-lead" : ""}`;

  if (!src || imgFailed) {
    return <div className={`${className} is-initials`}>{getInitials(name)}</div>;
  }

  return (
    <img
      src={src}
      alt=""
      className={className}
      onError={() => setImgFailed(true)}
    />
  );
};

const TopAchieversDesign4 = ({
  activeCategories,
  selectedWeek,
  weekOptions,
  currentData,
  currentLoading,
  excelLoading,
  handlePeriodChange,
  downloadExcel,
}) => {
  const sectionTitle = getSectionTitle(selectedWeek);

  return (
    <div className="card mb-4 custom-card border-0 rounded-4 top-achievers-section">
      {/* Top Banner Header */}
      <div
        className="card-header border-0 d-flex flex-wrap justify-content-between align-items-center gap-3"
        style={{
          backgroundColor: BRAND.primary,
          backgroundImage: GRADIENTS.topAchieversHeader,
          padding: "24px 32px",
          position: "relative",
          overflow: "hidden",
          borderTopLeftRadius: "16px",
          borderTopRightRadius: "16px",
        }}
      >
        <div style={{ position: "relative", zIndex: 1, minWidth: "220px" }}>
          <h2
            style={{
              color: "#fff",
              margin: 0,
              fontWeight: 700,
              fontSize: "1.4rem",
              letterSpacing: "-0.01em",
            }}
          >
            {sectionTitle}
          </h2>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginTop: "6px",
            }}
          >
            <p
              style={{
                color: "rgba(255, 255, 255, 0.85)",
                margin: 0,
                fontSize: "0.85rem",
                fontWeight: 500,
              }}
            >
              {getPeriodSubtitle(selectedWeek, currentData)}
            </p>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            position: "relative",
            zIndex: 1,
            flexWrap: "wrap",
          }}
        >
          <div className="ta-period-pills ta-period-pills--header" role="tablist">
            {PERIOD_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="tab"
                aria-selected={selectedWeek === opt.value}
                className={`ta-period-pill${
                  selectedWeek === opt.value ? " is-active" : ""
                }`}
                onClick={() => handlePeriodChange(opt.value)}
              >
                {weekOptions?.[opt.value]?.label || opt.label}
              </button>
            ))}
          </div>

          <button
            type="button"
            disabled={excelLoading}
            onClick={() => {
              if (!excelLoading) downloadExcel(activeCategories);
            }}
            style={{
              background: "#ffffff",
              border: "1px solid #ffffff",
              color: "#000000",
              padding: "8px 20px",
              borderRadius: "8px",
              fontSize: "0.85rem",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: "6px",
              cursor: excelLoading ? "not-allowed" : "pointer",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            }}
          >
            {excelLoading ? (
              <>
                Downloading... <i className="bx bx-loader-alt bx-spin"></i>
              </>
            ) : (
              <>
                Download{" "}
                <i className="bx bx-download" style={{ fontSize: "1.2rem" }}></i>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Cards Grid Body */}
      <div className="card-body p-4">
        {currentLoading ? (
          <div className="d-flex justify-content-center py-5">
            <OvalLoader color={BRAND.primary} />
          </div>
        ) : (
          <div className="ta-d4-grid">
            {activeCategories.map((cat) => {
              const list = (cat.list || []).slice(0, 3);
              const meta = CATEGORY_META[cat.id] || {
                subtitle: "Top performers",
                icon: "bx-trophy",
              };

              return (
                <div key={cat.id} className="ta-v-card">
                  <div className="ta-v-head">
                    <div className="ta-v-head-left">
                      <span className="ta-v-icon">
                        <i className={`bx ${meta.icon}`}></i>
                      </span>
                      <span className="ta-v-head-copy">
                        <span className="ta-v-title">{cat.title}</span>
                        <span className="ta-v-kicker">{meta.subtitle}</span>
                      </span>
                    </div>
                    <span className="ta-v-pill">Top 3</span>
                  </div>

                  {list.length > 0 ? (
                    <div className="ta-v-body">
                      {list.map((user, index) => (
                        <OverlayTrigger
                          key={`${cat.id}-${index + 1}`}
                          placement="top"
                          overlay={
                            <Tooltip
                              id={`ta-v-${cat.id}-${index + 1}`}
                              className="top-achiever-tooltip custom-brand-tooltip"
                            >
                              {tooltipText(user, cat, index + 1)}
                            </Tooltip>
                          }
                        >
                          <div className="ta-v-row" tabIndex={0}>
                            <span className="ta-v-rank">{index + 1}</span>
                            <div className="ta-v-avatar-wrap">
                              <MemberAvatar user={user} cat={cat} />
                            </div>
                            <div className="ta-v-user">
                              <div className="ta-v-name">
                                {getDisplayName(user, cat)}
                              </div>
                              <div className="ta-v-sub">{companyOf(user)}</div>
                            </div>
                            <div className="ta-v-row-metric">
                              {formatMetric(user, cat)}
                            </div>
                          </div>
                        </OverlayTrigger>
                      ))}

                      <Link
                        to={CATEGORY_REPORT_ROUTES[cat.id] || "/reports/overall"}
                        className="ta-v-link"
                      >
                        <span>View all {cat.title}</span>
                        <i className="bx bx-right-arrow-alt"></i>
                      </Link>
                    </div>
                  ) : (
                    <div className="ta-d4-empty">
                      <div className="ta-d4-empty-icon">
                        <i className="bx bx-file"></i>
                      </div>
                      <div className="ta-d4-empty-text">No data found!</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TopAchieversDesign4;
